import { basisPeriod } from "../domain/preparation";

export const EXTERNAL_COMPANY_UPLOAD_INSTRUCTIONS = [
  ["External company upload for Group Relief"],
  [""],
  ["Use one row for each company that is not in the TaxSavvy company list."],
  ["The imported companies are kept only in the current Group Relief case and are not saved to the database."],
  ["The case YA and FYE are applied automatically. Uploaded computations are treated as Single-YA and SGD."],
  ["Enter all allowance, loss and donation figures as positive whole-dollar amounts."],
  ["Enter a loss in Adjusted Profit / (Loss) as a negative whole-dollar amount."],
  ["Current-Year Losses may instead be entered as a positive amount in its own column when Adjusted Profit / (Loss) is 0. Do not enter both."],
  ["Qualifies for SUTE accepts TRUE or FALSE."],
  [""],
  ["Investment companies cannot include CA, current-year losses, prior-year losses or prior-year donations in this upload."],
  ["Use a saved Tax Computation when Cross-YA apportionment or a non-SGD currency is required."],
];

export const EXTERNAL_COMPANY_SAMPLE_DATA = [
  {
    "Company Name": "External Trading Pte. Ltd.",
    UEN: "202612345A",
    "Company Type": "Trading",
    "Adjusted Profit / (Loss)": 250000,
    "Prior-Year Unutilised CA": 0,
    "Current-Year CA": 30000,
    "Other-Sourced Income": 5000,
    "Prior-Year Unutilised Losses": 0,
    "Current-Year Losses": 0,
    "Prior-Year Unutilised Donations": 0,
    "Current-Year Donations": 2000,
    "Qualifies for SUTE": false,
  },
];

export const EXTERNAL_COMPANY_VALIDATION_SCHEMA = [
  { type: "text", required: true },
  { type: "text", required: true },
  {
    type: "text",
    required: true,
    fixedValue: ["Trading", "Investment"],
  },
  { type: "number", required: true },
  { type: "number", required: true },
  { type: "number", required: true },
  { type: "number", required: true },
  { type: "number", required: true },
  { type: "number", required: true },
  { type: "number", required: true },
  // Columns 11 and 12 are interpreted by buildExternalCompanyImport so both
  // the current template and the previous template remain readable.
  { required: true },
  { required: false },
];

const createId = () =>
  window.crypto?.randomUUID?.() ||
  `external-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const asWholeNumber = (value, label, { allowNegative = false } = {}) => {
  if (value === "" || value === null || value === undefined)
    throw new Error(`${label} is required.`);
  const rawValue = String(value).trim();
  const usesAccountingBrackets = /^\(.*\)$/.test(rawValue);
  const normalizedValue = (
    usesAccountingBrackets ? `-${rawValue.slice(1, -1)}` : rawValue
  ).replace(/,/g, "");
  const amount = Number(normalizedValue);
  if (!Number.isSafeInteger(amount))
    throw new Error(`${label} must be a whole-dollar amount.`);
  if (!allowNegative && amount < 0)
    throw new Error(`${label} must be zero or positive.`);
  return amount;
};

const deductionRow = (name, amount) =>
  amount
    ? [{ name, amount, taxAdjustment: "Deduct", status: "manual" }]
    : [];

const additionRow = (name, amount) =>
  amount ? [{ name, amount, taxAdjustment: "Add", status: "manual" }] : [];

const unutilisedRow = (name, amount, yearOfAssessment) =>
  amount
    ? [
        {
          name,
          reference: name,
          amount,
          yearOfAssessment: Number(yearOfAssessment) - 1,
          status: "manual",
          isForfeited: false,
        },
      ]
    : [];

export const externalCompanyInputToRow = (input) => [
  input.name,
  input.uen,
  input.companyType,
  input.adjustedProfit,
  input.priorYearCa,
  input.currentYearCa,
  input.otherSourcedIncome,
  input.priorYearLosses,
  input.currentYearLosses,
  input.priorYearDonations,
  input.currentYearDonations,
  input.qualifiesForSute,
];

const isBooleanCell = (value) =>
  value === true ||
  value === false ||
  ["true", "false"].includes(String(value || "").trim().toLowerCase());

export const buildExternalCompanyImport = (
  row,
  settings,
  identifiers = {},
) => {
  const [
    rawName,
    rawUen,
    rawCompanyType,
    rawAdjustedProfit,
    rawPriorYearCa,
    rawCurrentYearCa,
    rawOtherSourcedIncome,
    rawPriorYearLosses,
  ] = row;
  const usesLegacyColumnOrder =
    isBooleanCell(row[10]) && !isBooleanCell(row[11]);
  const [
    rawCurrentYearLosses,
    rawPriorYearDonations,
    rawCurrentYearDonations,
    rawQualifiesForSute,
  ] = usesLegacyColumnOrder
    ? [row[11], row[8], row[9], row[10]]
    : [row[8], row[9], row[10], row[11]];
  const name = String(rawName || "").trim();
  const uen = String(rawUen || "").trim();
  const companyType = String(rawCompanyType || "").trim();
  if (!name) throw new Error("Company Name is required.");
  if (!uen) throw new Error("UEN is required.");
  if (!["Trading", "Investment"].includes(companyType))
    throw new Error("Company Type must be Trading or Investment.");

  const adjustedProfit = asWholeNumber(
    rawAdjustedProfit,
    "Adjusted Profit / (Loss)",
    { allowNegative: true },
  );
  const currentYearLosses =
    rawCurrentYearLosses === "" ||
    rawCurrentYearLosses === null ||
    rawCurrentYearLosses === undefined
      ? 0
      : asWholeNumber(rawCurrentYearLosses, "Current-Year Losses");
  if (currentYearLosses > 0 && adjustedProfit !== 0)
    throw new Error(
      "Enter Current-Year Losses only when Adjusted Profit / (Loss) is zero.",
    );
  const effectiveAdjustedProfit =
    currentYearLosses > 0 ? -currentYearLosses : adjustedProfit;
  const priorYearCa = asWholeNumber(
    rawPriorYearCa,
    "Prior-Year Unutilised CA",
  );
  const currentYearCa = asWholeNumber(rawCurrentYearCa, "Current-Year CA");
  const otherSourcedIncome = asWholeNumber(
    rawOtherSourcedIncome,
    "Other-Sourced Income",
  );
  const priorYearLosses = asWholeNumber(
    rawPriorYearLosses,
    "Prior-Year Unutilised Losses",
  );
  const priorYearDonations = asWholeNumber(
    rawPriorYearDonations,
    "Prior-Year Unutilised Donations",
  );
  const currentYearDonations = asWholeNumber(
    rawCurrentYearDonations,
    "Current-Year Donations",
  );
  const normalizedSute = String(rawQualifiesForSute).trim().toLowerCase();
  if (
    rawQualifiesForSute !== true &&
    rawQualifiesForSute !== false &&
    normalizedSute !== "true" &&
    normalizedSute !== "false"
  )
    throw new Error("Qualifies for SUTE must be Yes or No.");
  const qualifiesForSute =
    rawQualifiesForSute === true || normalizedSute === "true";
  const investment = companyType === "Investment";
  if (
    investment &&
    [
      priorYearCa,
      currentYearCa,
      priorYearLosses,
      currentYearLosses,
      priorYearDonations,
    ].some(Boolean)
  )
    throw new Error(
      "Investment companies cannot include CA, current-year losses, prior-year losses or prior-year donations.",
    );

  const basis = basisPeriod(settings.commonFinancialYearEnd);
  if (!basis) throw new Error("Complete the Case FYE before uploading.");
  const id = identifiers.id || `external-${createId()}`;
  const computationId = identifiers.computationId || `external-tc-${id}`;
  const taxComputationType = investment ? "investment" : "common";
  const yearOfAssessment = Number(settings.yearOfAssessment);
  const externalInput = {
    name,
    uen,
    companyType,
    adjustedProfit,
    priorYearCa,
    currentYearCa,
    otherSourcedIncome,
    priorYearLosses,
    currentYearLosses,
    priorYearDonations,
    currentYearDonations,
    qualifiesForSute,
  };
  const currentYearDonationRows = deductionRow(
    "External upload - current-year donations",
    currentYearDonations,
  );
  const sourceDoc = {
    company: {
      name,
      uen,
      companyType: taxComputationType,
      taxComputationType,
      financialYearStart: basis.start,
      financialYearEnd: basis.end,
      yearOfAssessment,
    },
    currency: "SGD",
    currencyRate: 1,
    netProfitBeforeTax: effectiveAdjustedProfit,
    referenceData: {
      currentYearCapitalAllowances: deductionRow(
        "External upload - current-year CA",
        currentYearCa,
      ),
      currentYearDonations: currentYearDonationRows,
      otherSourceIncome: additionRow(
        "External upload - other-sourced income",
        otherSourcedIncome,
      ),
      taxAdjustmentSeparateSourcedIncome: investment
        ? additionRow(
            "External upload - other-sourced income",
            otherSourcedIncome,
          )
        : [],
    },
    unutilisedCapitalAllowances: unutilisedRow(
      "External upload - prior-year CA",
      priorYearCa,
      yearOfAssessment,
    ),
    unutilisedLosses: unutilisedRow(
      "External upload - prior-year losses",
      priorYearLosses,
      yearOfAssessment,
    ),
    unutilisedDonations: unutilisedRow(
      "External upload - prior-year donations",
      priorYearDonations,
      yearOfAssessment,
    ),
    taxPreviouslyAssessed: [],
    isStartup: qualifiesForSute,
  };

  return {
    company: {
      c_id: id,
      name,
      uen,
      companyType: taxComputationType,
      isExternal: true,
      externalSourceDoc: sourceDoc,
      externalInput,
      permission: { role: "external", is_partial_access: false },
    },
    computation: {
      tc_id: computationId,
      c_id: id,
      uen,
      companyType: taxComputationType,
      financialYearStart: basis.start,
      financialYearEnd: basis.end,
      yearOfAssessment,
      currencyRate: 1,
      currency_rate: 1,
      currency: "SGD",
      taxComputationType,
      taxPeriodType: "single_ya",
      basisPeriods: [],
      isCustomizedBasisPeriod: false,
      status: "external",
      isExternal: true,
    },
  };
};
